import React from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Text,
  Button,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { useAgency } from "#app/hooks/index.js";
import { resolveStrategy } from "#app/booking/strategies/index.js";
import { choosePreviewSite } from "./previewSite.js";

export function ParkMapAlert({ facility, siteType = null, ...props }) {
  const agency = useAgency();
  const strategy = resolveStrategy(agency.scanner);

  // The site whose reserve link stands for the facility: one the form's
  // current site type matches, so the provider opens on the tab the camper
  // is booking (see previewSite.js for why the first site is not enough).
  const previewSite = choosePreviewSite(facility.sites, siteType);
  // buildPreviewUrl takes a `site` plus an optional `facility` arg —
  // facility-level data (e.g. a park-level overview URL) is required for
  // chains where every site's reserveUrl is a too-specific room page.
  // Strategies that don't need the second arg simply ignore it.
  const mapUrl =
    previewSite?.reserveUrl != null
      ? strategy.buildPreviewUrl(previewSite, facility)
      : null;

  // Don't render if the strategy says no preview is available, if there's
  // no site, or if there's only one site (single-site facilities don't
  // need a "what sites does this place have?" alert).
  if (!mapUrl || facility.sites.length <= 1) {
    return null;
  }

  const alertText = useBreakpointValue({
    base: "Need help choosing?",
    sm: "Not sure what sites to pick?",
  });

  return (
    <Alert
      status="info"
      mb={4}
      borderRadius="md"
      colorScheme="gray"
      size="sm"
      {...props}
    >
      <AlertIcon />
      <Box flex="1">
        <Text fontSize="sm">{alertText}</Text>
      </Box>
      <Button
        as="a"
        href={mapUrl}
        target="_blank"
        rel="noopener noreferrer"
        variant="outline"
        size="xs"
        rightIcon={<ExternalLinkIcon />}
      >
        View map
      </Button>
    </Alert>
  );
}
